import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, Sparkles } from 'lucide-react';
import { Button } from '@repo/ui/src/components/ui/button';
import { Label } from '@repo/ui/src/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@repo/ui/src/components/ui/select';
import { cn } from '@repo/ui/src/lib/utils';
import type { ImportFieldMapping, ImportPayerMode, StudentImportAnalysis } from '@repo/shared-types';

const MAPPING_FIELDS: (keyof ImportFieldMapping)[] = [
  'studentName',
  'nickname',
  'dateOfBirth',
  'tags',
  'payerName',
  'payerPhone',
  'payerEmail',
];

const PAYER_MODES: ImportPayerMode[] = ['parent', 'self_pay'];

// Sentinel for "this field isn't in the file" — Select can't carry `null`.
const NONE = '__none__';

interface MappingStepProps {
  analysis: StudentImportAnalysis;
  onReanalyze: (override: {
    headerRowIndex: number;
    payerMode: ImportPayerMode;
    mapping: ImportFieldMapping;
  }) => void;
  onApplyAndNext: (override: {
    headerRowIndex: number;
    payerMode: ImportPayerMode;
    mapping: ImportFieldMapping;
  }) => void;
  onNext: () => void;
  isPending: boolean;
  /** THI-176 meters re-analyze the same as the first analyze, so THIS is the
   *  call that actually hits the monthly ai_credit cap on a repeat visit to
   *  this step (edit mapping → "Áp dụng lại"/"Tiếp tục") — ImportWizard used
   *  to pass no error prop here at all, so a 402 stopped the spinner and
   *  rendered nothing (THI-178 fix round, P2). Mirrors UploadStep's own
   *  error render exactly, including reusing its `upload.error` fallback —
   *  same underlying action, no reason for a second copy of the same string. */
  error: Error | null;
}

export function MappingStep({
  analysis,
  onReanalyze,
  onApplyAndNext,
  onNext,
  isPending,
  error,
}: MappingStepProps) {
  const { t } = useTranslation('studentImport');
  const [mapping, setMapping] = useState<ImportFieldMapping>(analysis.mapping);
  const [payerMode, setPayerMode] = useState<ImportPayerMode>(analysis.payerMode);

  // Re-sync local edits whenever a fresh analysis arrives (initial analyze,
  // or a completed "Áp dụng lại" re-analyze).
  useEffect(() => {
    setMapping(analysis.mapping);
    setPayerMode(analysis.payerMode);
  }, [analysis]);

  const setField = (field: keyof ImportFieldMapping, value: string) => {
    setMapping((prev) => ({ ...prev, [field]: value === NONE ? null : Number(value) }));
  };

  const dirty =
    JSON.stringify(mapping) !== JSON.stringify(analysis.mapping) || payerMode !== analysis.payerMode;

  return (
    <div className="py-2 space-y-5">
      {/* What the AI read — a finding, not a question. It used to be a bare line
          of text sitting directly above the payer toggle, and in a real
          walkthrough the two were read as one thought: "row 4 is missing a
          parent name, so it's asking me who pays". Neither control means that.
          The header row is where the column labels live, and the payer mode is
          a property of the whole file. Rows missing values are handled in the
          next step. Giving this its own tinted strip makes it read as output. */}
      <div className="flex items-start gap-2 rounded-md bg-brand-50 px-3 py-2.5 text-[13px] text-surface-700">
        <Sparkles className="mt-0.5 h-3.5 w-3.5 shrink-0 text-brand-600" />
        <span>
          {t('mapping.aiRead', {
            row: analysis.headerRowIndex + 1,
            count: analysis.summary.total,
          })}
        </span>
      </div>

      <div>
        <Label className="text-xs text-surface-500 mb-1.5 block">{t('mapping.payerModeLabel')}</Label>
        <div className="inline-flex rounded-md border border-surface-200 p-0.5">
          {PAYER_MODES.map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => setPayerMode(mode)}
              className={cn(
                'px-3 py-1.5 text-xs font-medium rounded-[5px] transition-colors',
                payerMode === mode ? 'bg-brand-500 text-white' : 'text-surface-600 hover:bg-surface-50',
              )}
            >
              {t(`mapping.payerMode.${mode}`)}
            </button>
          ))}
        </div>
        <p className="mt-1.5 text-xs text-surface-500">{t('mapping.payerModeHelp')}</p>
      </div>

      <div className="border-t pt-4">
        <Label className="text-xs text-surface-500 mb-3 block">{t('mapping.sectionColumns')}</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {MAPPING_FIELDS.map((field) => (
            <div key={field} className="grid gap-1.5">
              <Label className="text-xs text-surface-500">{t(`mapping.fields.${field}`)}</Label>
              <Select
                value={mapping[field] === null ? NONE : String(mapping[field])}
                onValueChange={(v) => setField(field, v)}
              >
                <SelectTrigger className="h-9">
                  <SelectValue placeholder={t('mapping.selectPlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>{t('mapping.none')}</SelectItem>
                  {analysis.headerLabels.map((label, idx) => (
                    <SelectItem key={idx} value={String(idx)}>
                      {label || t('mapping.columnFallback', { index: idx + 1 })}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            </div>
          ))}
        </div>
      </div>

      <div className="flex items-center justify-between pt-3 border-t">
        <Button
          type="button"
          variant="outline"
          className="gap-2"
          disabled={!dirty || isPending}
          onClick={() => onReanalyze({ headerRowIndex: analysis.headerRowIndex, payerMode, mapping })}
        >
          <RefreshCw className="h-4 w-4" />
          {isPending ? t('mapping.reapplying') : t('mapping.reapply')}
        </Button>
        <div className="flex items-center gap-3">
          {mapping.studentName === null && (
            <span className="text-xs text-amber-600">{t('mapping.needStudentName')}</span>
          )}
          {/* When the mapping was edited, "Tiếp tục" applies the override
              (re-runs classification) then advances — no dead-end. */}
          <Button
            type="button"
            disabled={mapping.studentName === null || isPending}
            onClick={() =>
              dirty
                ? onApplyAndNext({ headerRowIndex: analysis.headerRowIndex, payerMode, mapping })
                : onNext()
            }
          >
            {isPending ? t('mapping.reapplying') : t('mapping.next')}
          </Button>
        </div>
      </div>

      {error && <p className="text-sm text-red-600 text-center">{error.message || t('upload.error')}</p>}
    </div>
  );
}
